import {
    ActivityFilter,
    AuditChannel,
    AuditLogChangeType,
    ResourceType,
} from "@common/types/auditLog";
import {
    ALL_OBJECT_FIELD_OPTIONS,
    SCHEDULES_OBJECT_FIELD_VALUE,
    HistoryObjectFieldOption,
} from "./historyObjectFieldFilterOptions";

const NO_MATCH_FIELD = "__no_match__";

const NO_MATCH_GROUP: ActivityFilter = {
    resourceTypes: [ResourceType.TRANSACTION],
    fields: [NO_MATCH_FIELD],
};

export function buildActivityFilters(
    activities: HistoryObjectFieldOption[],
    changeTypes: AuditLogChangeType[] | undefined,
): ActivityFilter[] | undefined {
    if (activities.length === 0) {
        return undefined;
    }

    const shouldIncludeRelations = canIncludeRelations(changeTypes);
    const groups: ActivityFilter[] = [];

    for (const activity of activities) {
        if (activity.selfResourceTypes.length > 0) {
            groups.push({ resourceTypes: activity.selfResourceTypes });
        }

        if (!shouldIncludeRelations) {
            continue;
        }

        for (const relation of activity.relations) {
            groups.push({
                resourceTypes: [relation.resourceType],
                fields: relation.fields,
            });
        }
    }

    if (groups.length === 0) {
        return [NO_MATCH_GROUP];
    }

    return groups;
}

export interface ActivityFlagState {
    isSchedulesEnabled: boolean;
}

export function isActivityValueEnabled(
    value: string,
    { isSchedulesEnabled }: ActivityFlagState,
): boolean {
    if (value === SCHEDULES_OBJECT_FIELD_VALUE) {
        return isSchedulesEnabled;
    }
    return true;
}

export function excludeDisabledActivityValues(
    activityValues: string[] | undefined,
    flags: ActivityFlagState,
): string[] {
    if (!activityValues?.length) {
        return [];
    }

    return activityValues.filter((value) =>
        isActivityValueEnabled(value, flags),
    );
}

export interface ChannelFlagState {
    isAgentRunsEnabled: boolean;
}

export function isHistoryChannelEnabled(
    channel: AuditChannel,
    { isAgentRunsEnabled }: ChannelFlagState,
): boolean {
    if (channel === AuditChannel.AGENT) {
        return isAgentRunsEnabled;
    }
    return true;
}

/** The channel a row shows next to its actor: none when attribution is off or the channel's feature is. */
export function toVisibleChannel({
    channel,
    isChannelAttributionEnabled,
    isAgentRunsEnabled,
}: ChannelFlagState & {
    channel: AuditChannel | null | undefined;
    isChannelAttributionEnabled: boolean;
}): AuditChannel | undefined {
    return isChannelAttributionEnabled &&
        channel &&
        isHistoryChannelEnabled(channel, { isAgentRunsEnabled })
        ? channel
        : undefined;
}

export function excludeDisabledChannels(
    channels: AuditChannel[] | undefined,
    flags: ChannelFlagState,
): AuditChannel[] {
    if (!channels?.length) {
        return [];
    }

    return channels.filter((channel) =>
        isHistoryChannelEnabled(channel, flags),
    );
}

export function getActivitiesFromValues(
    activityValues: string[] | undefined,
): HistoryObjectFieldOption[] {
    if (!activityValues?.length) {
        return [];
    }

    return ALL_OBJECT_FIELD_OPTIONS.filter((option) =>
        activityValues.includes(option.value),
    );
}

export function canIncludeRelations(
    changeTypes: AuditLogChangeType[] | undefined,
): boolean {
    if (!changeTypes || changeTypes.length === 0) {
        return true;
    }

    return changeTypes.includes(AuditLogChangeType.UPDATE);
}
